import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";
import { currencyPattern } from "@acto/taskpane/core/utils/currencyPattern";

/** Every form an amount in `currency` can appear in: the glyph `locale` prints, and the ISO code. */
export const currencyTokens = (locale: string, currency: CurrencyCode): string[] => [
  currencyPattern(locale, currency).symbol,
  currency,
];

// The tenant currency comes from tenant settings, so any currency's glyph can reach a cell,
// and a glyph this class misses makes a real amount read as prose.
const NUMERIC_CELL_RE = /^[\d\s.,%+\-–—\p{Sc}]+$/u;

// A magnitude suffix counts only where it hangs off a digit ("1,2 Mio. €", "1.2M", "1.234 T€").
// Stripping the letters unanchored would classify a customer abbreviation like "MKB 12" as an
// amount. Multi-letter forms lead the alternation so "Mio" is not consumed as a bare "M".
const MAGNITUDE_RE = /(\d)\s?(?:Mio|Mrd|Tsd|bn|[KMBT])\.?/gi;

/**
 * Whether the cell is purely an amount — a number, currency or percentage ("1.538.632 €",
 * "8,62%", "1,2 Mio. €", "10000") — rather than prose that merely contains a figure.
 *
 * `tokens` are the only letter sequences allowed through: admitting letters in general would
 * classify "Muster GmbH 5" as an amount.
 */
export const isNumericCell = (raw: string, tokens: readonly string[]): boolean => {
  const value = raw.trim();
  if (!/\d/.test(value)) return false;

  const bare = tokens
    .reduce((acc, token) => acc.split(token).join(""), value)
    .replace(MAGNITUDE_RE, "$1")
    .trim();

  return NUMERIC_CELL_RE.test(bare);
};
